import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import {
  User, Envelope, Lock, Phone, MapPin, SignOut, Trash, ShieldCheck, Check, X, Eye, EyeSlash,
  Bell, Gear, CreditCard, Star, ChatCircle, Lightning, CaretRight, Globe, CurrencyDollar,
  Moon, Question, FileText, Eye as EyeIcon, Funnel, CalendarBlank, ArrowsClockwise, Devices,
  UploadSimple, PencilSimple, Warning
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Resolve an avatar URL: prepend the backend host ONLY for a root-relative
// uploaded path ("/uploads/…"). An absolute URL (a Google googleusercontent.com
// avatar) is already complete — prepending the host would corrupt it into
// "https://host/https://lh3…" and break the image.
const avatarSrc = (u) => (u ? (u.startsWith('/') ? `${API.replace('/api', '')}${u}` : u) : null);

// These presentational helpers live at MODULE scope, not inside ProfilePage.
// Defining a component inside another re-creates it on every render (every
// keystroke), which unmounts/remounts the inputs and drops focus — forcing a
// re-click for each letter. Module scope keeps them stable so inputs keep focus.
const Toggle = ({ checked, onChange, testId }) => (
  <button onClick={() => onChange(!checked)} className={`w-11 h-6 rounded-full transition-colors duration-300 relative shrink-0 cursor-pointer focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:outline-none ${checked ? 'bg-primary' : 'bg-border'}`} data-testid={testId}>
    <div className={`w-5 h-5 bg-card rounded-full shadow-soft absolute top-0.5 transition-transform duration-300 ${checked ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
  </button>
);

const SectionHead = ({ title, desc }) => (
  <div className="border-b border-border pb-4 mb-5">
    <h2 className="text-xl font-display font-semibold tracking-tight text-foreground">{title}</h2>
    {desc && <p className="text-sm text-muted-foreground mt-1">{desc}</p>}
  </div>
);

const InputField = ({ label, icon, children, ...rest }) => (
  <div>
    <label className="block text-sm font-medium text-foreground mb-1.5">{label}</label>
    <div className="relative">
      {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">{icon}</span>}
      {children || <input {...rest} className={`w-full h-11 rounded-xl border border-border bg-card ${icon ? 'pl-9' : 'pl-3'} pr-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40`} />}
    </div>
  </div>
);

const SIDEBAR = [
  { group: 'Account', items: [
    { id: 'personal', label: 'Personal Info', icon: <User size={16} /> },
    { id: 'security', label: 'Security', icon: <Lock size={16} /> },
    { id: 'payments', label: 'Payments & Billing', icon: <CreditCard size={16} /> },
  ]},
  { group: 'Activity', items: [
    { id: 'bookings', label: 'Bookings & Orders', icon: <CalendarBlank size={16} /> },
    { id: 'reviews', label: 'Reviews & Ratings', icon: <Star size={16} /> },
  ]},
  { group: 'Preferences', items: [
    { id: 'notifications', label: 'Notifications', icon: <Bell size={16} /> },
    { id: 'ai', label: 'AI Planner Prefs', icon: <Lightning size={16} /> },
    { id: 'privacy', label: 'Privacy', icon: <EyeIcon size={16} /> },
  ]},
  { group: 'More', items: [
    { id: 'appearance', label: 'Appearance', icon: <Moon size={16} /> },
    { id: 'help', label: 'Help & Support', icon: <Question size={16} /> },
    { id: 'account', label: 'Account Management', icon: <Gear size={16} /> },
  ]},
];

const EVENT_TYPES = ['Birthday', 'Wedding', 'Corporate', 'House party', 'Baby shower', 'Graduation', 'Anniversary', 'Holiday'];
const SERVICE_CATS = ['DJs', 'Photographers', 'Private Chefs', 'Caterers', 'Bartenders', 'Cleaners', 'Decorators', 'Videographers', 'Movers', 'Tutors'];

export default function ProfilePage() {
  const { user, setUser, token, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('personal');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [prefs, setPrefs] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [sessions, setSessions] = useState([]);

  // Form states
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [city, setCity] = useState(user?.city || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [deletePw, setDeletePw] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: '', text: '' }), 4000); };

  const loadPrefs = useCallback(async () => {
    try { const r = await axios.get(`${API}/profile/preferences`, { headers }); setPrefs(r.data); } catch (err) { console.error('Failed to load preferences:', err); }
  }, [headers]);

  useEffect(() => { loadPrefs(); }, [loadPrefs]);

  useEffect(() => {
    if (tab === 'bookings') axios.get(`${API}/profile/transactions`, { headers }).then(r => setTransactions(r.data)).catch(err => console.error('Transactions error:', err));
    if (tab === 'reviews') axios.get(`${API}/profile/reviews`, { headers }).then(r => setReviews(r.data)).catch(err => console.error('Reviews error:', err));
    if (tab === 'security') axios.get(`${API}/profile/sessions`, { headers }).then(r => setSessions(r.data)).catch(err => console.error('Sessions error:', err));
  }, [tab, headers]);

  const saveProfile = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const r = await axios.put(`${API}/profile`, { name, email, phone, city, bio }, { headers });
      setUser(r.data); flash('success', 'Profile updated');
    } catch (err) { flash('error', err.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { flash('error', 'Passwords do not match'); return; }
    if (newPw.length < 6) { flash('error', 'Minimum 6 characters'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/profile/password`, { current_password: currentPw, new_password: newPw }, { headers });
      setCurrentPw(''); setNewPw(''); setConfirmPw(''); flash('success', 'Password changed');
    } catch (err) { flash('error', err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const updatePref = async (key, value) => {
    setPrefs(p => ({ ...p, [key]: value }));
    try { await axios.put(`${API}/profile/preferences`, { [key]: value }, { headers }); } catch (err) { console.error('Failed to update preference:', err); }
  };

  const logoutAll = async () => {
    try { await axios.delete(`${API}/profile/sessions`, { headers }); await logout(); navigate('/'); } catch (err) { console.error('Failed to logout all:', err); }
  };

  const deleteAccount = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/profile`, { data: { password: deletePw }, headers });
      await logout(); navigate('/');
    } catch (err) { flash('error', err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const statusColor = (s) => ({ pending: 'bg-amber-50 text-amber-700', confirmed: 'bg-secondary text-primary', completed: 'bg-emerald-50 text-emerald-700', cancelled: 'bg-destructive/10 text-destructive' }[s] || 'bg-secondary text-muted-foreground');

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight text-foreground mb-1">Settings</h1>
        <p className="text-sm text-muted-foreground mb-8">Manage your account, security, and preferences</p>

        {msg.text && (
          <div className={`mb-5 px-4 py-3 text-sm rounded-xl border ${msg.type === 'success' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-destructive/5 text-destructive border-destructive/20'}`} data-testid="profile-flash">{msg.text}</div>
        )}

        <div className="grid md:grid-cols-5 gap-8">
          {/* Sidebar */}
          <div className="md:col-span-1 space-y-5" data-testid="profile-sidebar">
            {/* Avatar */}
            <div className="flex items-center gap-3 pb-5 border-b border-border">
              <div className="w-11 h-11 bg-secondary rounded-full flex items-center justify-center shrink-0 text-lg font-display font-medium text-primary overflow-hidden">
                {user?.avatar_url ? <img src={avatarSrc(user.avatar_url)} alt="" className="w-full h-full rounded-full object-cover" /> : user?.name?.[0]?.toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>

            {SIDEBAR.map(group => (
              <div key={group.group}>
                <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground/70 mb-1.5 px-3">{group.group}</p>
                {group.items.map(item => (
                  <button key={item.id} onClick={() => setTab(item.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 min-h-[44px] text-sm rounded-xl transition-colors duration-300 cursor-pointer ${tab === item.id ? 'bg-secondary text-primary font-medium' : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'}`}
                    data-testid={`tab-${item.id}`}>
                    {item.icon} {item.label}
                  </button>
                ))}
              </div>
            ))}
            <div className="pt-3 border-t border-border">
              <button onClick={async () => { await logout(); navigate('/'); }} className="w-full flex items-center gap-2.5 px-3 py-2.5 min-h-[44px] text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors duration-300 rounded-xl cursor-pointer" data-testid="sidebar-logout">
                <SignOut size={16} /> Sign out
              </button>
            </div>
          </div>

          {/* Main content */}
          <div className="md:col-span-4">
            {/* ─── PERSONAL INFO ─── */}
            {tab === 'personal' && (
              <form onSubmit={saveProfile} className="space-y-5 bg-card rounded-2xl border border-border/70 shadow-soft p-6 md:p-8 fade-up" data-testid="personal-form">
                <SectionHead title="Personal Information" desc="Update your profile details visible to others" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <InputField label="Full name" icon={<User size={15} />} type="text" value={name} onChange={e => setName(e.target.value)} data-testid="input-name" />
                  <InputField label="Email address" icon={<Envelope size={15} />} type="email" value={email} onChange={e => setEmail(e.target.value)} data-testid="input-email" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <InputField label="Phone number" icon={<Phone size={15} />} type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="(514) 555-0123" data-testid="input-phone" />
                  <InputField label="City" icon={<MapPin size={15} />} type="text" value={city} onChange={e => setCity(e.target.value)} placeholder="Your city" data-testid="input-city" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Bio</label>
                  <textarea value={bio} onChange={e => setBio(e.target.value)} rows={3} placeholder="Tell us about yourself..."
                    className="w-full rounded-xl border border-border bg-card px-3 py-2.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 resize-none" data-testid="input-bio" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">Profile photo</label>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center text-2xl font-display font-medium text-primary overflow-hidden">
                      {user?.avatar_url ? <img src={avatarSrc(user.avatar_url)} alt="" className="w-full h-full rounded-full object-cover" /> : user?.name?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <input type="file" id="avatar-upload" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 2 * 1024 * 1024) { flash('error', 'File too large. Max 2MB'); return; }
                          setSaving(true);
                          try {
                            const formData = new FormData();
                            formData.append('file', file);
                            const res = await axios.post(`${API}/profile/avatar`, formData, {
                              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                            });
                            setUser(res.data.user);
                            flash('success', 'Photo updated');
                          } catch (err) { flash('error', err.response?.data?.detail || 'Upload failed'); }
                          finally { setSaving(false); }
                        }}
                        data-testid="avatar-file-input" />
                      <button type="button" onClick={() => document.getElementById('avatar-upload').click()}
                        className="inline-flex items-center gap-1.5 border border-border bg-card text-foreground rounded-xl px-4 h-11 text-sm font-medium hover:bg-secondary/60 btn-anim cursor-pointer" data-testid="upload-photo">
                        <UploadSimple size={14} /> {saving ? 'Uploading...' : 'Upload photo'}
                      </button>
                      <p className="text-xs text-muted-foreground mt-1.5">JPG, PNG, WebP, GIF. Max 2MB</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-1">
                  <span className="text-xs text-muted-foreground">Account type</span>
                  <span className="rounded-full bg-secondary text-primary text-xs font-medium px-3 py-1 uppercase tracking-wider">{user?.role}</span>
                  {user?.role === 'customer' && (
                    <Link to="/provider/setup-profile" className="text-xs font-medium text-primary hover:underline">Switch to provider</Link>
                  )}
                </div>
                <button type="submit" disabled={saving} className="bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 disabled:opacity-50 cursor-pointer" data-testid="save-profile">{saving ? 'Saving...' : 'Save changes'}</button>
              </form>
            )}

            {/* ─── SECURITY ─── */}
            {tab === 'security' && (
              <div className="space-y-6" data-testid="security-section">
                <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-6 md:p-8 fade-up">
                  <SectionHead title="Change Password" desc="Keep your account secure with a strong password" />
                  <form onSubmit={changePassword} className="space-y-4 max-w-md">
                    <div className="relative">
                      <label className="block text-sm font-medium text-foreground mb-1.5">Current password</label>
                      <div className="relative"><Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input type={showCurrentPw ? 'text' : 'password'} value={currentPw} onChange={e => setCurrentPw(e.target.value)} className="w-full h-11 rounded-xl border border-border bg-card pl-9 pr-10 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-testid="input-current-pw" />
                        <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} aria-label="Toggle password visibility" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer">{showCurrentPw ? <EyeSlash size={16} /> : <Eye size={16} />}</button>
                      </div>
                    </div>
                    <div className="relative">
                      <label className="block text-sm font-medium text-foreground mb-1.5">New password</label>
                      <div className="relative"><Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input type={showNewPw ? 'text' : 'password'} value={newPw} onChange={e => setNewPw(e.target.value)} className="w-full h-11 rounded-xl border border-border bg-card pl-9 pr-10 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-testid="input-new-pw" />
                        <button type="button" onClick={() => setShowNewPw(!showNewPw)} aria-label="Toggle password visibility" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer">{showNewPw ? <EyeSlash size={16} /> : <Eye size={16} />}</button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1.5">Minimum 6 characters</p>
                    </div>
                    <div className="relative">
                      <label className="block text-sm font-medium text-foreground mb-1.5">Confirm new password</label>
                      <div className="relative"><Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                        <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} className="w-full h-11 rounded-xl border border-border bg-card pl-9 pr-10 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-testid="input-confirm-pw" />
                        {confirmPw && <div className="absolute right-3 top-1/2 -translate-y-1/2">{newPw === confirmPw ? <Check size={16} className="text-emerald-500" /> : <X size={16} className="text-destructive" />}</div>}
                      </div>
                    </div>
                    <button type="submit" disabled={saving} className="bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 disabled:opacity-50 cursor-pointer" data-testid="btn-change-pw">{saving ? 'Updating...' : 'Update password'}</button>
                  </form>
                </div>

                <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-6 md:p-8 fade-up" style={{ animationDelay: '50ms' }}>
                  <SectionHead title="Two-Factor Authentication" desc="Add an extra layer of security" />
                  <div className="rounded-xl border border-border/70 p-4 max-w-md flex items-center justify-between">
                    <div><p className="text-sm font-medium text-foreground">2FA via authenticator app</p><p className="text-xs text-muted-foreground mt-0.5">Protect your account with TOTP codes</p></div>
                    <span className="rounded-full bg-secondary text-muted-foreground text-xs font-medium px-3 py-1">Coming soon</span>
                  </div>
                </div>

                <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-6 md:p-8 fade-up" style={{ animationDelay: '100ms' }}>
                  <SectionHead title="Login Activity" desc="Devices currently signed in" />
                  <div className="space-y-2 max-w-md">
                    {sessions.length === 0 ? <p className="text-sm text-muted-foreground">No active sessions found</p> : sessions.slice(0, 5).map((s, i) => (
                      <div key={i} className="rounded-xl border border-border/70 p-3.5 flex items-center justify-between hover:bg-secondary/50 transition-colors duration-300">
                        <div className="flex items-center gap-2.5"><Devices size={16} className="text-primary" />
                          <div><p className="text-sm font-medium text-foreground">Session {i + 1}</p><p className="text-xs text-muted-foreground">Created: {new Date(s.created_at).toLocaleDateString()}</p></div>
                        </div>
                      </div>
                    ))}
                    <button onClick={logoutAll} className="text-sm text-destructive hover:text-destructive/80 font-medium mt-2 min-h-[44px] cursor-pointer" data-testid="btn-logout-all">Log out of all devices</button>
                  </div>
                </div>

                <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-6 md:p-8 fade-up" style={{ animationDelay: '150ms' }}>
                  <SectionHead title="Connected Accounts" desc="Manage linked sign-in methods" />
                  <div className="space-y-2 max-w-md">
                    <div className="rounded-xl border border-border/70 p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors duration-300">
                      <div className="flex items-center gap-3">
                        <svg width="18" height="18" viewBox="0 0 18 18"><path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/><path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/></svg>
                        <div><p className="text-sm font-medium text-foreground">Google</p><p className="text-xs text-muted-foreground">{user?.avatar_url ? 'Connected' : 'Not connected'}</p></div>
                      </div>
                      <span className={`rounded-full text-xs font-medium px-3 py-1 ${user?.avatar_url ? 'bg-emerald-50 text-emerald-700' : 'bg-secondary text-muted-foreground'}`}>{user?.avatar_url ? 'Linked' : 'Available'}</span>
                    </div>
                    <div className="rounded-xl border border-border/70 p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors duration-300">
                      <div className="flex items-center gap-3"><Envelope size={18} className="text-primary" /><div><p className="text-sm font-medium text-foreground">Email & Password</p><p className="text-xs text-muted-foreground">{user?.email}</p></div></div>
                      <span className="rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium px-3 py-1">Active</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ─── PAYMENTS ─── */}
            {tab === 'payments' && (
              <div className="space-y-6" data-testid="payments-section">
                <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-6 md:p-8 fade-up">
                  <SectionHead title="Payments & Billing" desc="Manage payment methods and view transaction history" />
                  <div className="max-w-md">
                    <div className="rounded-xl border border-border/70 bg-secondary/30 p-5 flex items-start gap-3">
                      <ShieldCheck size={22} className="text-primary shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-foreground">No card stored on file</p>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">You enter your card securely at checkout when you book — payment is processed by Stripe and held in escrow until the service is complete. Bookr never stores your card number.</p>
                      </div>
                    </div>
                  </div>

                  {user?.role === 'provider' && (
                    <div className="mt-6">
                      <h3 className="text-sm font-semibold text-foreground mb-3">Payout methods</h3>
                      <Link
                        to="/provider/dashboard?payouts=1"
                        className="rounded-xl border border-border/70 p-4 max-w-md flex items-center justify-between hover:bg-secondary/40 transition-colors cursor-pointer"
                      >
                        <div><p className="text-sm font-medium text-foreground">Direct deposit</p><p className="text-xs text-muted-foreground mt-0.5">Connect your bank via Stripe to get paid after each job</p></div>
                        <span className="rounded-full bg-primary/10 text-primary text-xs font-medium px-3 py-1 flex items-center gap-1">Set up <CaretRight size={12} weight="bold" /></span>
                      </Link>
                    </div>
                  )}
                </div>

                <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-6 md:p-8 fade-up" style={{ animationDelay: '50ms' }}>
                  <h3 className="text-sm font-semibold text-foreground mb-3">Transaction history</h3>
                  {transactions.length === 0 ? (
                    <div className="rounded-xl border border-border/70 p-8 text-center"><p className="text-sm text-muted-foreground">No transactions yet</p></div>
                  ) : (
                    <div className="rounded-xl border border-border/70 overflow-hidden">
                      <table className="w-full text-sm"><thead className="bg-secondary/50 border-b border-border">
                        <tr>
                          <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-widest text-muted-foreground font-medium">Service</th>
                          <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-widest text-muted-foreground font-medium">Date</th>
                          <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-widest text-muted-foreground font-medium">Amount</th>
                          <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-widest text-muted-foreground font-medium">Status</th>
                        </tr></thead><tbody>
                        {transactions.map((t, i) => (
                          <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-secondary/40 transition-colors duration-300">
                            <td className="px-4 py-3"><p className="font-medium text-foreground text-xs">{t.provider_name}</p><p className="text-xs text-muted-foreground">{t.service_title}</p></td>
                            <td className="px-4 py-3 text-xs text-muted-foreground">{t.booking_date}</td>
                            <td className="px-4 py-3 text-xs font-medium text-foreground tabular-nums">${t.total_paid}</td>
                            <td className="px-4 py-3"><span className={`rounded-full text-xs font-medium px-2.5 py-0.5 capitalize ${statusColor(t.status)}`}>{t.status}</span></td>
                          </tr>
                        ))}
                      </tbody></table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ─── BOOKINGS ─── */}
            {tab === 'bookings' && (
              <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-6 md:p-8 fade-up" data-testid="bookings-section">
                <SectionHead title="Bookings & Orders" desc="View and manage your service bookings" />
                {transactions.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
                      <CalendarBlank size={26} className="text-primary" />
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">No bookings yet — your event plans start here.</p>
                    <Link to="/browse" className="inline-flex items-center justify-center bg-primary text-primary-foreground rounded-xl px-6 h-11 text-sm font-semibold btn-anim shadow-soft hover:shadow-lift hover:bg-primary/90 cursor-pointer">Browse services</Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.map((b, i) => (
                      <div key={i} className="rounded-xl border border-border/70 p-4 flex items-center justify-between hover:bg-secondary/50 transition-colors duration-300 fade-up" style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }}>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-medium text-foreground">{b.provider_name}</p>
                            <span className={`rounded-full text-xs font-medium px-2.5 py-0.5 capitalize ${statusColor(b.status)}`}>{b.status}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">{b.service_title}</p>
                          <p className="text-xs text-muted-foreground mt-1">{b.booking_date} &middot; {b.start_time || ''} &middot; {b.location_address || 'No address'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-base font-display font-semibold text-foreground tabular-nums">${b.total_paid}</p>
                          {b.status === 'completed' && <Link to="/browse" className="text-xs font-medium text-primary hover:underline">Book again</Link>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ─── REVIEWS ─── */}
            {tab === 'reviews' && (
              <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-6 md:p-8 fade-up" data-testid="reviews-section">
                <SectionHead title="Reviews & Ratings" desc={user?.role === 'provider' ? 'Reviews from your customers' : 'Reviews you\'ve left'} />
                {reviews.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
                      <Star size={26} className="text-primary" />
                    </div>
                    <p className="text-sm text-muted-foreground">No reviews yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {reviews.map((r, i) => (
                      <div key={i} className="rounded-xl border border-border/70 p-4 hover:bg-secondary/50 transition-colors duration-300 fade-up" style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }}>
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex">{[1,2,3,4,5].map(s => <Star key={s} size={14} weight={s <= r.rating ? 'fill' : 'regular'} className={s <= r.rating ? 'text-amber-400' : 'text-border'} />)}</div>
                          <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
                        </div>
                        <p className="text-sm text-foreground leading-relaxed">{r.comment || 'No comment'}</p>
                        <p className="text-xs text-muted-foreground mt-2">{user?.role === 'provider' ? `By ${r.customer_name}` : `For ${r.provider_name}`}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ─── NOTIFICATIONS ─── */}
            {tab === 'notifications' && prefs && (
              <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-6 md:p-8 fade-up" data-testid="notifications-section">
                <SectionHead title="Notification Preferences" desc="Choose how and when you get notified" />
                <div className="space-y-6 max-w-lg">
                  {[
                    { title: 'Booking Updates', items: [
                      { key: 'notif_booking_email', label: 'Email notifications', desc: 'Confirmations, cancellations, status changes' },
                      { key: 'notif_booking_push', label: 'Push notifications', desc: 'Real-time alerts in your browser' },
                      { key: 'notif_booking_sms', label: 'SMS notifications', desc: 'Text message alerts for bookings' },
                    ]},
                    { title: 'Messages', items: [
                      { key: 'notif_messages_email', label: 'Email for new messages', desc: 'Get emailed when a provider messages you' },
                      { key: 'notif_messages_push', label: 'Push for new messages', desc: 'Real-time message alerts' },
                    ]},
                    { title: 'Other', items: [
                      { key: 'notif_payment_email', label: 'Payment confirmations', desc: 'Receipts and payout notifications' },
                      { key: 'notif_ai_updates', label: 'AI planner updates', desc: 'When your AI plan has new recommendations' },
                      { key: 'notif_promo_email', label: 'Promotions & offers', desc: 'Special deals and product updates' },
                    ]},
                  ].map(section => (
                    <div key={section.title}>
                      <h3 className="text-sm font-semibold text-foreground mb-3">{section.title}</h3>
                      <div className="space-y-2">
                        {section.items.map(n => (
                          <div key={n.key} className="rounded-xl border border-border/70 p-4 min-h-[44px] flex items-center justify-between gap-4 hover:bg-secondary/50 transition-colors duration-300">
                            <div><p className="text-sm font-medium text-foreground">{n.label}</p><p className="text-xs text-muted-foreground mt-0.5">{n.desc}</p></div>
                            <Toggle checked={!!prefs[n.key]} onChange={v => updatePref(n.key, v)} testId={`toggle-${n.key}`} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ─── AI PREFERENCES ─── */}
            {tab === 'ai' && prefs && (
              <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-6 md:p-8 fade-up" data-testid="ai-prefs-section">
                <SectionHead title="AI Planner Preferences" desc="Customize how the AI builds your event packages" />
                <div className="space-y-6 max-w-lg">
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-3">Default budget range</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className="block text-sm font-medium text-foreground mb-1.5">Min ($)</label>
                        <input type="number" value={prefs.ai_budget_min || 500} onChange={e => updatePref('ai_budget_min', Number(e.target.value))} className="w-full h-11 rounded-xl border border-border bg-card px-3 text-sm tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-testid="ai-budget-min" /></div>
                      <div><label className="block text-sm font-medium text-foreground mb-1.5">Max ($)</label>
                        <input type="number" value={prefs.ai_budget_max || 5000} onChange={e => updatePref('ai_budget_max', Number(e.target.value))} className="w-full h-11 rounded-xl border border-border bg-card px-3 text-sm tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40" data-testid="ai-budget-max" /></div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-3">Preferred event types</h3>
                    <div className="flex flex-wrap gap-2">
                      {EVENT_TYPES.map(et => {
                        const selected = (prefs.ai_event_types || []).includes(et);
                        return <button key={et} onClick={() => updatePref('ai_event_types', selected ? (prefs.ai_event_types || []).filter(x => x !== et) : [...(prefs.ai_event_types || []), et])}
                          className={`rounded-full px-4 py-2 min-h-[38px] text-xs font-medium border btn-anim cursor-pointer ${selected ? 'bg-primary text-primary-foreground border-primary shadow-soft' : 'bg-card text-muted-foreground border-border hover:bg-secondary/60 hover:text-foreground'}`} data-testid={`event-type-${et.toLowerCase()}`}>{et}</button>;
                      })}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground mb-3">Preferred service categories</h3>
                    <div className="flex flex-wrap gap-2">
                      {SERVICE_CATS.map(sc => {
                        const selected = (prefs.ai_preferred_categories || []).includes(sc);
                        return <button key={sc} onClick={() => updatePref('ai_preferred_categories', selected ? (prefs.ai_preferred_categories || []).filter(x => x !== sc) : [...(prefs.ai_preferred_categories || []), sc])}
                          className={`rounded-full px-4 py-2 min-h-[38px] text-xs font-medium border btn-anim cursor-pointer ${selected ? 'bg-primary text-primary-foreground border-primary shadow-soft' : 'bg-card text-muted-foreground border-border hover:bg-secondary/60 hover:text-foreground'}`} data-testid={`cat-pref-${sc.toLowerCase().replace(/\s/g, '-')}`}>{sc}</button>;
                      })}
                    </div>
                  </div>
                  <div className="rounded-xl border border-border/70 p-4 min-h-[44px] flex items-center justify-between gap-4 max-w-lg hover:bg-secondary/50 transition-colors duration-300">
                    <div><p className="text-sm font-medium text-foreground">Auto-suggestions</p><p className="text-xs text-muted-foreground mt-0.5">Let AI suggest providers based on your preferences</p></div>
                    <Toggle checked={!!prefs.ai_auto_suggest} onChange={v => updatePref('ai_auto_suggest', v)} testId="toggle-ai-auto" />
                  </div>
                </div>
              </div>
            )}

            {/* ─── PRIVACY ─── */}
            {tab === 'privacy' && prefs && (
              <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-6 md:p-8 fade-up" data-testid="privacy-section">
                <SectionHead title="Privacy Controls" desc="Control who can see your information" />
                <div className="space-y-2 max-w-lg">
                  {[
                    { key: 'privacy_show_profile', label: 'Show my profile publicly', desc: 'Other users can find and view your profile' },
                    { key: 'privacy_show_phone', label: 'Show phone number', desc: 'Visible to providers after booking confirmation' },
                    { key: 'privacy_show_email', label: 'Show email address', desc: 'Visible to providers after booking confirmation' },
                  ].map(p => (
                    <div key={p.key} className="rounded-xl border border-border/70 p-4 min-h-[44px] flex items-center justify-between gap-4 hover:bg-secondary/50 transition-colors duration-300">
                      <div><p className="text-sm font-medium text-foreground">{p.label}</p><p className="text-xs text-muted-foreground mt-0.5">{p.desc}</p></div>
                      <Toggle checked={!!prefs[p.key]} onChange={v => updatePref(p.key, v)} testId={`toggle-${p.key}`} />
                    </div>
                  ))}
                </div>
                <div className="mt-6 max-w-lg">
                  <h3 className="text-sm font-semibold text-foreground mb-3">Data & usage</h3>
                  <div className="rounded-xl bg-secondary/50 border border-border/70 p-4">
                    <p className="text-xs text-muted-foreground leading-relaxed">Bookr.ai uses your data to personalize recommendations, improve search results, and optimize the AI planner. We never sell your personal information to third parties. <span className="text-primary font-medium underline cursor-pointer">Read our Privacy Policy</span></p>
                  </div>
                </div>
              </div>
            )}

            {/* ─── APPEARANCE ─── */}
            {tab === 'appearance' && (
              <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-6 md:p-8 fade-up" data-testid="appearance-section">
                <SectionHead title="Appearance" desc="Customize how Bookr.ai looks for you" />
                <div className="space-y-4 max-w-lg">
                  <div className="rounded-xl border border-border/70 p-4 min-h-[44px] flex items-center justify-between gap-4 hover:bg-secondary/50 transition-colors duration-300">
                    <div className="flex items-center gap-3"><Moon size={18} className="text-primary" /><div><p className="text-sm font-medium text-foreground">Dark mode</p><p className="text-xs text-muted-foreground mt-0.5">Coming soon</p></div></div>
                    <span className="rounded-full bg-secondary text-muted-foreground text-xs font-medium px-3 py-1">Soon</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Language</label>
                    <select defaultValue="en" className="w-full h-11 rounded-xl border border-border bg-card px-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 cursor-pointer" data-testid="language-select">
                      <option value="en">English</option><option value="fr">Fran&ccedil;ais</option><option value="es">Espa&ntilde;ol</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Currency</label>
                    <select defaultValue="cad" className="w-full h-11 rounded-xl border border-border bg-card px-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 cursor-pointer" data-testid="currency-select">
                      <option value="cad">CAD ($)</option><option value="usd">USD ($)</option><option value="eur">EUR (&euro;)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* ─── HELP ─── */}
            {tab === 'help' && (
              <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-6 md:p-8 fade-up" data-testid="help-section">
                <SectionHead title="Help & Support" desc="Get help with your account or the platform" />
                <div className="space-y-3 max-w-lg">
                  {[
                    { title: 'Help Center', desc: 'Browse FAQs and guides', icon: <Question size={18} /> },
                    { title: 'Contact Support', desc: 'Get help from our team', icon: <ChatCircle size={18} /> },
                    { title: 'Report a Problem', desc: 'Let us know about bugs or issues', icon: <Warning size={18} /> },
                  ].map((h, i) => (
                    <div key={i} className="rounded-xl border border-border/70 p-4 min-h-[44px] flex items-center justify-between cursor-pointer hover:bg-secondary/50 transition-colors duration-300 fade-up" style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }}>
                      <div className="flex items-center gap-3"><span className="text-primary">{h.icon}</span><div><p className="text-sm font-medium text-foreground">{h.title}</p><p className="text-xs text-muted-foreground mt-0.5">{h.desc}</p></div></div>
                      <CaretRight size={14} className="text-muted-foreground" />
                    </div>
                  ))}
                  <div className="border-t border-border pt-4 mt-4 space-y-2">
                    <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Legal</p>
                    <div className="flex gap-4">
                      <span className="text-xs text-muted-foreground hover:text-primary cursor-pointer transition-colors">Terms of Service</span>
                      <span className="text-xs text-muted-foreground hover:text-primary cursor-pointer transition-colors">Privacy Policy</span>
                      <span className="text-xs text-muted-foreground hover:text-primary cursor-pointer transition-colors">Provider Agreement</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ─── ACCOUNT MANAGEMENT ─── */}
            {tab === 'account' && (
              <div className="bg-card rounded-2xl border border-border/70 shadow-soft p-6 md:p-8 fade-up" data-testid="account-section">
                <SectionHead title="Account Management" desc="Manage your account settings" />
                <div className="space-y-5 max-w-lg">
                  <div className="rounded-xl border border-border/70 bg-secondary/30 p-5">
                    <div className="space-y-2.5 text-sm">
                      <div className="flex justify-between"><span className="text-muted-foreground">User ID</span><span className="text-foreground font-mono text-xs">{user?.user_id}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Role</span><span className="text-foreground capitalize font-medium">{user?.role}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Email</span><span className="text-foreground font-medium">{user?.email}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Member since</span><span className="text-foreground font-medium">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}</span></div>
                    </div>
                  </div>
                  <div className="rounded-xl border border-border/70 p-5">
                    <h3 className="text-sm font-semibold text-foreground mb-1">Sign out of all devices</h3>
                    <p className="text-xs text-muted-foreground mb-3">Invalidate all active sessions</p>
                    <button onClick={logoutAll} className="inline-flex items-center border border-border bg-card text-foreground rounded-xl px-4 h-11 text-sm font-medium hover:bg-secondary/60 btn-anim cursor-pointer" data-testid="btn-signout-all">Sign out everywhere</button>
                  </div>
                  <div className="rounded-xl bg-destructive/5 border border-destructive/20 p-5 mt-8">
                    <h3 className="text-sm font-semibold text-destructive mb-1 flex items-center gap-1.5"><Warning size={15} /> Delete account</h3>
                    <p className="text-xs text-muted-foreground mb-3">Permanently delete your account and all data. This cannot be undone.</p>
                    {!deleteConfirm ? (
                      <button onClick={() => setDeleteConfirm(true)} className="inline-flex items-center border border-destructive/30 text-destructive rounded-xl px-4 h-11 text-sm font-medium hover:bg-destructive/10 btn-anim cursor-pointer" data-testid="btn-delete-start">Delete my account</button>
                    ) : (
                      <div className="space-y-3">
                        <input type="password" value={deletePw} onChange={e => setDeletePw(e.target.value)} placeholder="Enter password to confirm" className="w-full h-11 rounded-xl border border-destructive/20 bg-card px-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive/40" data-testid="input-delete-pw" />
                        <div className="flex gap-2">
                          <button onClick={deleteAccount} disabled={!deletePw || saving} className="inline-flex items-center bg-destructive text-destructive-foreground rounded-xl px-4 h-11 text-sm font-semibold hover:bg-destructive/90 disabled:opacity-50 btn-anim cursor-pointer" data-testid="btn-delete-confirm">{saving ? 'Deleting...' : 'Permanently delete'}</button>
                          <button onClick={() => { setDeleteConfirm(false); setDeletePw(''); }} className="inline-flex items-center border border-border bg-card text-muted-foreground rounded-xl px-4 h-11 text-sm font-medium hover:bg-secondary/60 btn-anim cursor-pointer">Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
